<template>
	<div class="form-input form-select" :class="[
                sizeableString ? 'form-input-' + sizeableString : '', {
                'is-active': isActive,
                'is-required': isRequired,
                'is-empty': isEmpty,
                'has-error': hasError,
                'has-warning': !hasError && props.warning,
                'is-inverted': props.inverted,
                'is-plain': props.plain,
                'is-disabled': props.disabled,
                'has-suffix': !props.plain,
            }]">
		<div v-if="props.onlyOptions">
			<div v-if="props.filterable || props.allowCreate" class="w-full flex mb-12">
				<form-input class="flex-grow-x-1" :large="props.large" :x-large="props.xLarge" :xx-large="props.xxLarge" :small="props.small" :x-small="props.xSmall"
							:enterkeyhint="props.allowCreate ? 'enter' : 'search'" :type="props.filterInputType"
							ref="searchInputComponent"
							:placeholder="props.searchPlaceholder ? props.searchPlaceholder : $t('action.search')" hide-details v-model="filterValue" :autofocus="props.autofocus"
							clearable
							inverted/>
				<slot name="filter-buttons"/>
			</div>
			<div v-if="props.loading">
				<div v-for="skeletonIndex in skeletonLimit" :key="'skeleton-' + skeletonIndex" class="flex justify-between items-center w-full border-box px-8"
					 :style="{height: dropdownOptionHeight}">
					<skeleton :height="optionAvatarSize" width="100%" rounded/>
				</div>
			</div>
			<div v-else-if="!displayOptionItems || !displayOptionItems.length" class="w-full p-8 text-sm text-gray-400">{{ missingOptionsText }}</div>
			<template v-else>
				<template v-for="(option, optionIndex) in displayOptionItems" :key="getSafePropValue(props.valueProp, option) + '-' + optionIndex">
					<div
						v-if="props.groupProp && getSafePropValue(props.groupProp, option) && (optionIndex === 0 || getSafePropValue(props.groupProp, option) !== getSafePropValue(props.groupProp, displayOptionItems[optionIndex-1]))"
						class="px-8 pb-4 font-bold text-sm" :class="{'pt-12': optionIndex > 0}">{{ getSafePropValue(props.groupProp, option) }}
					</div>
					<form-select-option :height-style="dropdownOptionHeight" :color="props.color"
										:selected="isSelected(option)"
										:focused="optionIndex === arrowSelectIndex"
										:disabled="option.disabled"
										:multiple="props.multiple"
										:text="getSafePropValue(props.textProp, option)"
										:image="props.imageProp ? option[props.imageProp] : undefined"
										:image-size="optionAvatarSize"
										:image-ratio="props.imageRatio"
										:image-tile="props.imageTile"
										:icon="props.iconProp ? option[props.iconProp] : undefined"
										:help="getSafePropValue(props.subtitleProp, option)"
										@toggle="toggleOption(option)"/>
				</template>
			</template>
			<slot name="below_options" v-bind:heightStyle="dropdownOptionHeight"/>
		</div>
		<template v-else>
			<div class="form-input__content" ref="inputContentElement" :style="{borderColor: borderColor}">
				<dropdown :root="inputContentElement" v-model="isActive" ref="dropdown" :disabled="props.disabled" :max-height="304" :fix-mobile-height="props.filterable" attach-width>
					<div class="form-input__field cursor-pointer" :class="{'mr-24': !props.plain}">
						<span ref="focusElement" @focus="handleFocus" @blur="handleBlur" tabindex="0"></span>
						<span v-if="props.label" class="form-input__label">{{ props.label }}</span>
						<span v-if="!optionSelected && placeholderText" class="form-input__placeholder">{{ placeholderText }}</span>
						<div v-else-if="selectedOptions.length" class="w-full">
							<template v-if="$slots.selected">
								<slot name="selected" v-bind:options="selectedOptions"/>
							</template>
							<template v-else-if="props.multiple && props.orderable">
								<draggable :list="selectedOptions" :item-key="props.valueProp" @update="handleDraggedOption">
									<template #item="{element}">
										<chip :small="!props.xLarge" close
											  :disabled="element.disabled"
											  @click:close="unselectOption(element)" class="mr-4 my-2 cursor-move">
											<Icon v-if="props.iconProp && element[props.iconProp]" :icon="element[props.iconProp]" small color="gray-400" class="mr-10"/>
											{{ getSafePropValue(props.textProp, element) }}
										</chip>
									</template>
								</draggable>
							</template>
							<template v-else-if="props.multiple">
								<chip v-for="(option,optionIndex) in selectedOptions" :key="getSafePropValue(props.valueProp, option) + '-' + optionIndex" :small="!props.xLarge"
									  close
									  :disabled="option.disabled"
									  @click:close="unselectOption(option)" class="mr-4 my-2">
									<Icon v-if="props.iconProp && option[props.iconProp]" :icon="option[props.iconProp]" small color="gray-400" class="mr-10"/>
									{{ getSafePropValue(props.textProp, option) }}
								</chip>
							</template>
							<template v-else>
								<div v-for="(option, optionIndex) in selectedOptions" :key="getSafePropValue(props.valueProp, option) + '-' + optionIndex"
									 class="flex items-center">
									<avatar v-if="props.imageProp && option[props.imageProp] !== undefined" :src="option[props.imageProp]" :size="optionAvatarSize"
											class="mr-10"
											contain
											:round="!props.imageTile && (option[props.imageProp]?.ratio ?? props.imageRatio) === 1"
											:tile="props.imageTile"
											:ratio="(option[props.imageProp]?.ratio ?? props.imageRatio)"/>
									<Icon v-else-if="props.iconProp && option[props.iconProp]" :icon="option[props.iconProp]" small color="gray-400" class="mr-10"/>
									<span class="truncate">{{ getSafePropValue(props.textProp, option) }}</span>
								</div>
							</template>
						</div>
					</div>

					<div v-if="!props.plain" class="form-input__suffix">
						<div v-if="props.clearable && optionSelected" @click.stop="unselectAll">
							<Icon :icon="icons.reset" class="cursor-pointer mr-8"/>
						</div>

						<Icon x-small force-size :icon="dropIcon"/>

						<tooltip v-if="props.help" :text="props.help" left>
							<Icon :icon="icons.help"/>
						</tooltip>
					</div>

					<template v-slot:title="{isMobile}">
						<span v-if="isMobile && (props.label || placeholderText)" class="slide-sheet__title">{{ props.label ?? placeholderText }}</span>
						<span v-if="isMobile && props.help" class="slide-sheet__description">{{ props.help }}</span>

						<div v-if="props.filterable || props.allowCreate" class="w-full border-box px-8 ">

							<!-- Do not autofocus search as this disables input navigation via tabs since the input is not in the form tree. -->
							<!-- Instead we manually focus the component via key listeners. -->
							<form-input class="my-8 w-full" :enterkeyhint="props.allowCreate ? 'enter' : 'search'"
										:type="props.filterInputType"
										ref="searchInputComponent"
										:placeholder="props.searchPlaceholder ? props.searchPlaceholder : (props.allowCreate ? $t('placeholder.search_or_create') : $t('placeholder.search'))"
										hide-details v-model="filterValue"
										clearable
										inverted/>
						</div>
					</template>

					<template v-slot:menu="{isMobile}">
						<template v-if="props.loading">
							<div v-for="skeletonIndex in skeletonLimit" :key="'skeleton-' + skeletonIndex" class="flex justify-between items-center w-full border-box px-12"
								 :style="{height: isMobile ? '48px' : dropdownOptionHeight}">
								<skeleton :height="optionAvatarSize" width="100%" rounded/>
							</div>
						</template>
						<div v-else-if="!displayOptionItems || !displayOptionItems.length" class="w-full p-16 text-sm border-box text-gray-400">{{ missingOptionsText }}</div>
						<template v-else>
							<template v-for="(option, optionIndex) in displayOptionItems" :key="getSafePropValue(props.valueProp, option) + '-' + optionIndex">
								<div
									v-if="props.groupProp && getSafePropValue(props.groupProp, option) && (optionIndex === 0 || getSafePropValue(props.groupProp, option) !== getSafePropValue(props.groupProp, displayOptionItems[optionIndex-1]))"
									class="px-12 pb-4 font-bold text-sm" :class="{'pt-12': optionIndex > 0}">{{ getSafePropValue(props.groupProp, option) }}
								</div>

								<form-select-option :height-style="isMobile ? '48px' : dropdownOptionHeight"
													:color="props.color"
													:selected="isSelected(option)"
													:focused="optionIndex === arrowSelectIndex"
													:disabled="option.disabled"
													:multiple="props.multiple"
													:text="getSafePropValue(props.textProp, option)"
													:image="props.imageProp ? option[props.imageProp] : undefined"
													:image-size="optionAvatarSize"
													:image-ratio="props.imageRatio"
													:image-tile="props.imageTile"
													:icon="props.iconProp ? option[props.iconProp] : undefined"
													:help="getSafePropValue(props.subtitleProp, option)"
													@toggle="toggleOption(option)"/>
							</template>
						</template>
					</template>
					<template v-slot:footer="{isMobile}">
						<slot name="below_options" v-bind:heightStyle="isMobile ? '48px' : dropdownOptionHeight"/>
					</template>
				</dropdown>
			</div>
			<div v-if="!hideDetails" class="form-input__details">
				<span v-if="hasError && errorMessage" class="form-input__message" tabindex="-1">{{ errorMessage }}</span>
				<span v-else-if="props.warning" class="form-input__message" tabindex="-1">{{ props.warning }}</span>
				<span v-else-if="infoText" class="form-input__message" tabindex="-1">{{ infoText }}</span>
				<span v-else-if="$slots.info" class="form-input__message" tabindex="-1"><slot name="info"/></span>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">

import {mdiChevronDown, mdiChevronUp} from "@mdi/js";
import {cloneObject, debounce, deepEqual, getPropValue, isObject} from "@spoferan/spoferan-ts-core";
import {checkIcon, helpIcon, resetIcon} from "../../../../config";
import type {Ref} from 'vue';
import {computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from "#imports";
import {TextInputComponentEmits, type TextInputComponentProps, useTextInputComponent} from "../../../../composables/useTextInputComponent";

const {t} = useI18n();
const draggable = defineAsyncComponent(() => import('vuedraggable'));

const optionAvatarSize = 20;

const props = withDefaults(defineProps<{
	options: (object[]) | number,

	// Whether multiple-select is activated
	// Keeps the select options dropdown open after a value was selected
	multiple?: boolean,

	// Whether multiple selected options can be ordered by dragging them
	orderable?: boolean,

	filterable?: boolean,

	// Whether the options must match the exact filter query; a fuzzy search is applied otherwise
	exactFilter?: boolean,

	// The text of the additional option to unselect the selection with the value 'null'
	unselect?: string | object,

	textProp?: string,
	valueProp?: string,
	helpProp?: string,
	subtitleProp?: string,
	imageProp?: string,
	iconProp?: string,

	// The key of the select options that defines a group of options
	groupProp?: string,

	imageRatio?: number,
	imageTile?: boolean,

	// Disables the original input field and shows the options by default
	onlyOptions?: boolean,

	// Whether custom values are allowed
	allowCreate?: boolean,

	// The value to use to store a new created option value
	createValue?: string,

	searchPlaceholder?: string,

	filterInputType?: string,

	// A color to style the background of the selected option
	color?: string,

	// The selected option objects to keep in sync
	selected?: object,

	// Whether the options are currently being loaded
	loading?: boolean,

	// The max amount of skeletons to display when the component is in a loading state
	skeletons?: number,

	// A custom function that receives the filter query and returns the options to display
	filterFunction?: Function
} & TextInputComponentProps>(), {
	textProp: 'text',
	valueProp: 'value',
	helpProp: 'help',
	subtitleProp: 'subtitle',
	imageProp: 'image',
	iconProp: 'icon',
	groupProp: 'group',
	imageRatio: 1,
	filterInputType: 'search',
	skeletons: 8
});

const emit = defineEmits([...TextInputComponentEmits, 'update:createValue', 'update:selected', 'select']);

const inputElement: Ref<HTMLElement | null> = ref(null);
const inputContentElement: Ref<HTMLElement | null> = ref(null);
const searchInputComponent: Ref<HTMLElement | null> = ref(null);

// The HTML element used to focus the select component
const focusElement: Ref<Element | null> = ref(null);

const submitCreateValue = ref(props.createValue);
watch(() => props.createValue, (newValue) => {
	submitCreateValue.value = newValue;
});
watch(submitCreateValue, () => {
	hasInput.value = true;
	emit('update:createValue', submitCreateValue)
	if (shouldValidate.value) {
		nextTick(validate)
	}
});
const validationValue = computed(() => {
	return props.allowCreate && props.createValue && props.createValue.length ? props.createValue : props.modelValue
});

const {
	submitValue,
	sizeableString,
	isActive,
	isRequired,
	hasError,
	hasInput,
	hasPrefix,
	errorMessage,
	shouldValidate,
	validate,
	handleKeydown,
	handleBlur,
	handleFocus,
	reset,
	clearValue
} = useTextInputComponent(inputElement, props, emit, {
	validationValue: validationValue,
	clearValue: () => {
		submitValue.value = Array.isArray(submitValue.value) ? [] : undefined
		submitCreateValue.value = Array.isArray(submitCreateValue.value) ? [] : undefined
	}
});

watch(submitValue, (newValue) => {
	if (newValue) {
		submitCreateValue.value = null;
	}
});

// The input value to filter the options for selection
const filterValue: Ref<string | null> = ref(null);

// The index of the option that is currently selected by the keyboard arrows
const arrowSelectIndex = ref(-1);

const icons = {
	reset: resetIcon,
	help: helpIcon,
	check: checkIcon
};

const skeletonLimit = computed(() => {
	return Math.min(optionItems.value.length, props.skeletons);
});

const dropIcon = computed(() => {
	return isActive.value ? mdiChevronUp : mdiChevronDown;
});

const missingOptionsText = computed(() => {
	if (filterValue.value && filterValue.value.length) {
		return t('missing.options.search', {search: filterValue.value}, `Für die Suchanfrage wurden keine Auswahlmöglichkeiten gefunden.`);
	}

	return props.allowCreate ? t('missing.options.index_create', `Noch keine Auswahlmöglichkeiten vorhanden. Tippe den gewünschten Begriff ein, um eine neue Auswahlmöglichkeit anzulegen.`) : t('missing.options.index', `Keine Auswahlmöglichkeiten vorhanden.`)
});

const selectedOptions = computed(() => {
	let selectedValues = Array.isArray(submitValue.value) ? submitValue.value : [submitValue.value];
	selectedValues = selectedValues.filter(value => value !== undefined);

	// If no submit value exists, we check if a create value exists that can be selected
	// We do not select both (submitValue and submitCreateValue) as the submitCreateValue might be set to the same value as submitValue, e.g., on FormClubSelect
	if ((!selectedValues.length || (selectedValues.length === 1 && selectedValues[0] === null)) && submitCreateValue.value) {
		selectedValues = Array.isArray(submitCreateValue.value) ? submitCreateValue.value : [submitCreateValue.value];
		selectedValues = selectedValues.filter(value => ![null, undefined].includes(value));
	}

	// We loop through the selected values to keep the selected order
	const options = [];
	for (let i = 0; i < selectedValues.length; i++) {
		// We want to support numeric value that are set as strings (parseFloat will transform numeric integers to floats without decimals -> integers)
		const foundOption = selectableOptionItems.value.find(item => {
			if (getSafePropValue(props.valueProp, item) === selectedValues[i]) {
				return true;
			}
			// If we are dealing with numbers, they might be equal when compared as float
			if (!isNaN(selectedValues[i]) && !isNaN(getSafePropValue(props.valueProp, item))) {
				return parseFloat(getSafePropValue(props.valueProp, item)) === parseFloat(selectedValues[i]);
			}

			return false;
		});
		if (foundOption) {
			options.push(foundOption);
		}
	}

	return options
});

const placeholderText = computed(() => {
	return props.placeholder ?? (props.rules?.required ? t('placeholder.select') : null)
});

const isEmpty = computed(() => {
	return !optionSelected.value && !placeholderText.value;
});

const optionSelected = computed(() => {
	return !!(selectedOptions.value && selectedOptions.value.length);
});

const selectedOptionHelp = computed(() => {
	if (selectedOptions.value && props.helpProp) {
		let option = optionSelected.value ? selectedOptions.value[0] : selectedOptions.value;
		if (option && option[props.helpProp]) {
			return option[props.helpProp];
		}
	}

	return null
});

const infoText = computed(() => {
	return selectedOptionHelp.value ? selectedOptionHelp.value : props.info
});

const unselectOptionItem = computed(() => {
	if (props.unselect) {
		const optionItem = {};
		setPropValue(props.textProp, optionItem, isObject(props.unselect) ? props.unselect[props.textProp] : props.unselect);
		if (props.subtitleProp && isObject(props.unselect)) {
			setPropValue(props.subtitleProp, optionItem, props.unselect[props.subtitleProp] ?? null);
		}
		if (props.imageProp && isObject(props.unselect)) {
			setPropValue(props.imageProp, optionItem, props.unselect[props.imageProp] ?? null);
		}
		setPropValue(props.valueProp, optionItem, null);

		return optionItem;
	}

	return undefined;
});

const createOptionItem = computed(() => {
	if (props.allowCreate && submitCreateValue.value && submitCreateValue.value.length) {
		const createOption: { [key: string]: any } = {};
		setPropValue(props.textProp, createOption, submitCreateValue.value);
		setPropValue(props.valueProp, createOption, submitCreateValue.value);
		createOption._created = true

		return createOption;
	}

	return undefined;
});

const optionItems = computed(() => {
	return getOptionItems(props.options);
});

const selectableOptionItems = computed(() => {
	const options = [];

	if (unselectOptionItem.value) {
		options.push(unselectOptionItem.value)
	}

	options.push(...optionItems.value)

	// We insert the custom create value at the bottom to prefer selecting existing options
	if (createOptionItem.value) {
		options.push(createOptionItem.value)
	}

	return options;
});

// The filtered options to display
const filteredOptionItems: Ref<object[]|null> = ref(null); // Null when not filtered yet

const displayOptionItems = computed(() => {
	 if (!filterValue.value?.length || !filteredOptionItems.value) {
		 return selectableOptionItems.value;
	 }

	const options = [];

	if (unselectOptionItem.value) {
		options.push(unselectOptionItem.value)
	}

	options.push(...filteredOptionItems.value)

	// We insert the custom create value at the bottom to prefer selecting existing options
	if (createOptionItem.value) {
		options.push(createOptionItem.value)
	}

	return options;
});

const dropdownOptionHeight = computed(() => {
	switch (sizeableString) {
		case 'sm':
			return '32px';
		case 'lg':
			return '40px';
		case 'xl':
			return '44px';
	}

	return '36px';
});

watch(isActive, (newValue) => {
	if (newValue) {
		enableControls();
		handleFocus();
	} else {
		if (submitCreateValue.value && submitCreateValue.value.length) {
			selectCreateValueOption();
		}

		disableControls();
		handleBlur();
		filterValue.value = '';
	}
});

watch(filterValue, (newValue) => {

	// Auto set the filter value as creation value for improved UX
	if (props.allowCreate && newValue?.length) {
		submitCreateValue.value = newValue;
		submitValue.value = null
	}

	refreshFilteredOptions();
});

watch(selectedOptions, (newValue, oldValue) => {
	if (!deepEqual(newValue, oldValue)) {
		emit('update:selected', props.multiple ? newValue : (newValue.length ? newValue[0] : null))
	}
}, {immediate: true});

function refreshFilteredOptions() {
	if (!filterValue.value?.length) {
		filteredOptionItems.value = optionItems.value;
	} else if (props.filterFunction) {
		refreshFilteredOptionsByCustomFilter();
	} else {
		filteredOptionItems.value = optionItems.value.filter(item => {
			return doesMatchFilter(getSafePropValue(props.textProp, item), filterValue.value) || doesMatchFilter(getSafePropValue(props.valueProp, item), filterValue.value) || doesMatchFilter(getSafePropValue(props.subtitleProp, item), filterValue.value) || doesMatchFilter(getSafePropValue(props.groupProp, item), filterValue.value)
		})
	}
}

const refreshFilteredOptionsByCustomFilter = debounce(async () => {
	filteredOptionItems.value = getOptionItems(await props.filterFunction(filterValue.value));
}, 300);

function toggleOption(option) {
	if (option.disabled) {
		return;
	}

	const storeValue = option._created ? submitCreateValue.value : submitValue.value;
	if (props.multiple && storeValue && storeValue.length && storeValue.indexOf(getSafePropValue(props.valueProp, option)) >= 0) {
		unselectOption(option);
	} else {
		selectOption(option);
	}
}

function selectOption(option) {
	if (option.disabled) {
		return;
	}

	if (window.native_vibrate) {
		window.native_vibrate.postMessage('select')
	}

	const value = getSafePropValue(props.valueProp, option);
	const isCreatedOption = option._created;
	if (props.multiple) {
		const storeData = isCreatedOption ? submitCreateValue : submitValue;
		if (!storeData.value) {
			storeData.value = [value];
		} else if (Array.isArray(storeData.value)) {
			storeData.value.push(value)
		} else {
			storeData.value = storeData.value.length ? [storeData.value, value] : [value];
		}
	} else {
		if (isCreatedOption) {
			submitCreateValue.value = value;
			submitValue.value = null
		} else {
			submitCreateValue.value = null;
			submitValue.value = value
		}
	}

	// Let the user select multiple items without closing when only the options are displayed or explicitly specified
	if (!props.onlyOptions && !props.multiple) {
		focusElement.value.focus(); // Focus the focus element to allow the user to navigate to the next input via tab
		isActive.value = false;
		filterValue.value = null;
	}

	// On next tick to have the submitValue updated on the parents, e.g., form data
	nextTick(function () {
		emit('select', option)
	});
}

function unselectOption(item) {
	// Disabled options can not be unselected
	if (item.disabled) {
		return;
	}

	if (window.native_vibrate) {
		window.native_vibrate.postMessage('select')
	}

	const storeData = item._created ? submitCreateValue : submitValue;
	if (props.multiple && Array.isArray(storeData.value) && storeData.value.length) {
		const index = storeData.value.indexOf(getSafePropValue(props.valueProp, item));
		if (index >= 0) {
			storeData.value.splice(index, 1)
		}
	} else {
		storeData.value = null;
	}

	// Let the user select multiple items without closing when only the options are displayed or explicitly specified
	if (!props.onlyOptions && !props.multiple) {
		isActive.value = false;
	}
}

function selectCreateValueOption() {
	if (!submitCreateValue.value?.length) {
		return;
	}
	const filterOption = selectableOptionItems.value.find(option => stripValueForSearch(getSafePropValue(props.textProp, option)) === stripValueForSearch(submitCreateValue.value));
	if (filterOption) {
		selectOption(filterOption);
	}
}

function stripValueForSearch(value) {
	if (value === null || value === undefined) {
		return '';
	}

	return value.toString().toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Resets the selected values
function unselectAll() {
	reset();
	emit('select', null)
}

// Lets the user search for options and control the select while typing on the keyboard
function enableControls() {
	document.addEventListener('keydown', preventKeydownEvents);
	document.addEventListener('keyup', handleControlEvent);
}

// Prevents the user from searching and controlling the select while typing on the keyboard
function disableControls() {
	document.removeEventListener('keydown', preventKeydownEvents);
	document.removeEventListener('keyup', handleControlEvent);
}

function preventKeydownEvents(event) {
	// Prevent window scrolling on arrows
	if ([37, 38, 39, 40].indexOf(event.keyCode) > -1) {
		event.preventDefault();
	}
}

function handleControlEvent(event) {
	if (!event || !event.key) {
		return;
	}

	const key = event.key.toLowerCase();

	switch (key) {
		case 'escape':
			if (!props.onlyOptions) {
				isActive.value = false;
			}
			break;
		case 'enter':
			const wasSearching = filterValue.value && filterValue.value.length;

			// If an item is explicitly highlighted with arrow keys, we select this option
			if (arrowSelectIndex.value > -1) {
				const option = displayOptionItems.value[arrowSelectIndex.value];
				if (option) {
					selectOption(option);
				}
			} else if (submitCreateValue.value && submitCreateValue.value.length) {
				selectCreateValueOption();
			}

			// Re-focus the search input, if the user searched before
			if (wasSearching && props.multiple && searchInputComponent.value) {
				searchInputComponent.value.focus();
			}
			arrowSelectIndex.value = -1;

			break;
		case 'arrowdown':
			// When press the arrow down button for the first time but the first option is already selected, we initially start selecting the second option
			if (arrowSelectIndex.value === -1 && displayOptionItems.value.length && displayOptionItems.value[0][props.valueProp] === submitValue.value) {
				arrowSelectIndex.value = 1;
			} else {
				arrowSelectIndex.value = arrowSelectIndex.value === (displayOptionItems.value.length - 1) ? 0 : arrowSelectIndex.value + 1;
			}
			break;
		case 'arrowup':
			arrowSelectIndex.value = arrowSelectIndex.value === 0 ? (displayOptionItems.value.length - 1) : (arrowSelectIndex.value - 1);
			break;
		case 'tab':
			// When the user tries to navigate to the next input, we need to ensure that the focus is on the focus element of the FormSelect component to focus the next input.
			// This might be not the case when the user currently filters the options and has the filter input focused which is out of the focus DOM.
			if (document.activeElement !== focusElement.value) {
				focusElement.value.focus();
			}
			break;
		default:

			// When the user tries to filter the options, we focus the filter input with the user's input for filtering
			if (key.length === 1 && searchInputComponent.value && !searchInputComponent.value.isActive) {
				filterValue.value = filterValue.value ? (filterValue.value + key) : key;
				searchInputComponent.value.focus();

				// We need to re-activate the FormSelect component as the focus event on the search input will deactivate it (blur) which would cause the options dropdown to close
				isActive.value = true;
			}

	}
	handleKeydown(event);
}

/**
 * Gets valid option objects based on the given basic option data.
 * Extracting this in a method enables us to easily modify the option items
 * in child components, e.g., FormGenderInput
 *
 * @param options
 * @returns {[]}
 */
function getOptionItems(options) {
	const items = [];
	if (options && !Array.isArray(options) && !isNaN(options)) {
		(new Array(options)).fill(null).forEach((option, key) => {
			const optionItem = {};
			setPropValue(props.textProp, optionItem, key + 1);
			setPropValue(props.valueProp, optionItem, key + 1);
			items.push(optionItem);
		})
	} else {
		options = cloneObject(options); // We need to clone to no accidentally modify the root object
		options.forEach(option => {
			if (isObject(option)) {
				items.push(option)
			} else {
				const optionItem = {};
				setPropValue(props.textProp, optionItem, option);
				setPropValue(props.valueProp, optionItem, option);
				items.push(optionItem);
			}
		})
	}

	return items;
}

function isSelected(item) {
	return (Array.isArray(submitValue.value) ? submitValue.value.indexOf(getSafePropValue(props.valueProp, item)) > -1 : submitValue.value === getSafePropValue(props.valueProp, item)) || (Array.isArray(submitCreateValue.value) ? submitCreateValue.value.indexOf(getSafePropValue(props.valueProp, item)) > -1 : submitCreateValue.value && submitCreateValue.value === getSafePropValue(props.valueProp, item))
}

function getSafePropValue(prop, option) {
	return prop ? getPropValue(option, prop) : null
}

function setPropValue(prop, option, value) {
	if (!prop) {
		return;
	}
	const props = prop.split('.');
	for (let i = 0; i < props.length; i++) {
		if (i === (props.length - 1)) {
			option[props[i]] = value
		} else {
			if (!option[props[i]]) {
				option[props[i]] = {}
			}
			option = option[props[i]];
		}
	}
}

function handleDraggedOption(event) {
	submitValue.value.splice(event.newIndex, 0, submitValue.value.splice(event.oldIndex, 1)[0]);
}

function doesMatchFilter(targetValue, filterValue) {
	if (!targetValue || !filterValue) {
		return false;
	}

	if (props.exactFilter) {
		return targetValue.toString().toLowerCase() === filterValue.toLowerCase();
	}

	return stripValueForSearch(targetValue).includes(stripValueForSearch(filterValue));
}

onMounted(() => {
	if (props.onlyOptions) {
		isActive.value = true;
	}
});

onBeforeUnmount(() => {
	disableControls();
});

defineExpose({
	clearValue
})

</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.form-input {
  width: 100%;
  position: relative;
}
.form-input .form-input__content {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  background-color: #ffffff;
  display: flex;
  position: relative;
  align-items: center;
}
.form-input .form-input__content > label, .form-input .form-input__content > .form-input__field-wrapper, .form-input .form-input__content > .dropdown, .form-input .form-input__content > .dropdown > .dropdown__activator {
  height: 100%;
  width: 100%;
  min-height: inherit;
  display: flex;
  align-items: center;
  position: relative;
}
.form-input .form-input__content .form-input__label {
  position: absolute;
  font-size: 15px;
  top: 10px;
  line-height: 1.2;
  left: 4px;
  background: #ffffff;
  color: #5e6b6b;
  padding: 0 4px;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  transform-origin: top left;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.form-input .form-input__content .form-input__field {
  width: 100%;
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  min-height: 38px;
  padding: 4px 8px;
  border: none;
  outline: none;
  color: #525e5e;
  background-color: #ffffff;
  border-radius: 5px;
  display: flex;
  align-items: center;
}
.form-input .form-input__content .form-input__field .form-input__placeholder {
  color: #7D8484;
}
.form-input .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field[type=search] {
  -webkit-appearance: none;
}
.form-input .form-input__content .form-input__field::-webkit-search-cancel-button {
  display: none;
}
.form-input .form-input__content .form-input__field-content {
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  color: #525e5e;
}
.form-input .form-input__details {
  display: flex;
  width: 100%;
  height: 16px;
  margin-bottom: 8px;
  position: relative;
}
.form-input .form-input__details > .form-input__message {
  font-size: 11px;
  line-height: 1.1;
  text-align: left;
  font-weight: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px 8px;
}
.form-input .form-input__details > .form-input__message:focus, .form-input .form-input__details > .form-input__message:hover {
  overflow: unset;
  text-overflow: unset;
  white-space: unset;
  position: absolute;
  z-index: 1;
  background: #ffffff;
}
.form-input.is-active .form-input__content {
  border-color: #008200;
}
.form-input.is-required .form-input__label:after {
  content: " *";
  color: #FA5555;
}
.form-input.is-disabled {
  opacity: 0.8;
}
.form-input.is-disabled .form-input__field {
  cursor: not-allowed;
}
.form-input:not(.is-empty) .form-input__content .form-input__label, .form-input.is-active .form-input__content .form-input__label {
  left: 4px !important;
  transform: translateY(-18px) scale(0.7);
  max-width: 120%;
}
.form-input:not(.is-empty).has-suffix .form-input__content .form-input__label, .form-input.is-active.has-suffix .form-input__content .form-input__label {
  padding-right: 0;
}
.form-input.has-success .form-input__content {
  border-color: #47a447 !important;
}
.form-input.has-success .form-input__message {
  color: #47a447;
}
.form-input.has-success .form-input__label {
  color: #47a447;
}
.form-input.has-error .form-input__content {
  border-color: #FA5555 !important;
}
.form-input.has-error .form-input__message {
  color: #FA5555;
}
.form-input.has-error .form-input__label {
  color: #FA5555;
}
.form-input.has-warning .form-input__content {
  border-color: #EB9E05 !important;
}
.form-input.has-warning .form-input__message {
  color: #EB9E05;
}
.form-input.has-warning .form-input__label {
  color: #EB9E05;
}
.form-input.has-prefix .form-input__content .form-input__field-wrapper {
  padding-left: 32px;
}
.form-input.has-prefix .form-input__content .form-input__label {
  left: 32px;
}
.form-input.has-prefix .form-input__prefix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-prefix .form-input__prefix .icon, .form-input.has-prefix .form-input__prefix .icon > svg {
  width: 18px;
  height: 18px;
}
.form-input.has-suffix .form-input__content .form-input__field-wrapper, .form-input.has-suffix .form-input__content .form-input__label {
  padding-right: 32px;
}
.form-input.has-suffix .form-input__suffix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-suffix .form-input__suffix .icon, .form-input.has-suffix .form-input__suffix .icon > svg {
  width: 18px;
  height: 18px;
  color: #7D8484;
}
.form-input.is-inverted .form-input__content {
  border-color: #f4f4f5;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field {
  color: #525e5e;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field .form-input__placeholder {
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__label {
  background: transparent;
}
.form-input.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.is-plain .form-input__content {
  border: none;
}
.form-input.is-plain .form-input__content .form-input__field {
  padding: 0 !important;
}
.form-input.is-plain .form-input__details .form-input__message {
  padding-left: 0;
  padding-right: 0;
}
.form-input.form-input-xs .form-input__content {
  min-height: 24px;
}
.form-input.form-input-xs .form-input__content .form-input__label {
  font-size: 11.5px;
  top: 5.1px;
}
.form-input.form-input-xs .form-input__content .form-input__field {
  min-height: 24px;
  font-size: 11.5px;
}
.form-input.form-input-xs .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xs.has-prefix .form-input__prefix {
  top: 13px;
}
.form-input.form-input-xs.has-suffix .form-input__suffix {
  top: 13px;
}
.form-input.form-input-xs:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-active .form-input__content .form-input__label {
  transform: translateY(-11px) scale(0.7);
}
.form-input.form-input-xs.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-17px) scale(0.7);
}
.form-input.form-input-sm .form-input__content {
  min-height: 32px;
}
.form-input.form-input-sm .form-input__content .form-input__label {
  font-size: 12px;
  top: 8.8px;
}
.form-input.form-input-sm .form-input__content .form-input__field {
  min-height: 32px;
  font-size: 12px;
}
.form-input.form-input-sm .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-sm.has-prefix .form-input__prefix {
  top: 17px;
}
.form-input.form-input-sm.has-suffix .form-input__suffix {
  top: 17px;
}
.form-input.form-input-sm:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-active .form-input__content .form-input__label {
  transform: translateY(-15px) scale(0.7);
}
.form-input.form-input-sm.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-21px) scale(0.7);
}
.form-input.form-input-md .form-input__content {
  min-height: 38px;
}
.form-input.form-input-md .form-input__content .form-input__label {
  font-size: 15px;
  top: 10px;
}
.form-input.form-input-md .form-input__content .form-input__field {
  min-height: 38px;
  font-size: 15px;
}
.form-input.form-input-md .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-md.has-prefix .form-input__prefix {
  top: 20px;
}
.form-input.form-input-md.has-suffix .form-input__suffix {
  top: 20px;
}
.form-input.form-input-md:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-active .form-input__content .form-input__label {
  transform: translateY(-18px) scale(0.7);
}
.form-input.form-input-md.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-lg .form-input__content {
  min-height: 46px;
}
.form-input.form-input-lg .form-input__content .form-input__label {
  font-size: 15px;
  top: 14px;
}
.form-input.form-input-lg .form-input__content .form-input__field {
  min-height: 46px;
  font-size: 15px;
}
.form-input.form-input-lg .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-lg.has-prefix .form-input__prefix {
  top: 24px;
}
.form-input.form-input-lg.has-suffix .form-input__suffix {
  top: 24px;
}
.form-input.form-input-lg:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-active .form-input__content .form-input__label {
  transform: translateY(-22px) scale(0.7);
}
.form-input.form-input-lg.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-28px) scale(0.7);
}
.form-input.form-input-xl .form-input__content {
  min-height: 50px;
}
.form-input.form-input-xl .form-input__content .form-input__label {
  font-size: 16px;
  top: 15.4px;
}
.form-input.form-input-xl .form-input__content .form-input__field {
  min-height: 50px;
  font-size: 16px;
}
.form-input.form-input-xl .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xl.has-prefix .form-input__prefix {
  top: 26px;
}
.form-input.form-input-xl.has-suffix .form-input__suffix {
  top: 26px;
}
.form-input.form-input-xl:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-xl.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-30px) scale(0.7);
}

.form-input.form-select .form-input__content .form-input__field {
  padding-right: 24px;
}
</style>